import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { generateFCMToken } from "../../firebase";

import ErrorText from "../../components/Typography/ErrorText";
import InputText from "../../components/Input/InputText";
import axios from "axios";
import { ErrorAlert, SuccessAlert } from "../../components/Alerts/SuccessAlert";
import { endPoints } from "../../utils/endPoints";
import { Eye, EyeOff } from "lucide-react";

function SchoolTeacherLogin() {
  const INITIAL_LOGIN_OBJ = {
    password: "",
    emailId: "",
  };

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loginObj, setLoginObj] = useState(INITIAL_LOGIN_OBJ);
  const [showPassword, setShowPassword] = useState(false);

  let navigate = useNavigate();

  const submitForm = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    let { emailId, password } = loginObj;
    try {
      if (emailId === "") {
        return setEmailError("Email Id is required!");
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailId)) {
        return setErrorMessage("Please enter a valid Email Id!");
      }

      if (password === "") {
        return setErrorMessage("Password is required!");
      }

      // ✅ Strong Password Validation
      // const passwordRegex =
      //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

      // if (!passwordRegex.test(password)) {
      //   return setErrorMessage(
      //     "Password must be at least 8 characters and include uppercase, lowercase, number, and special character!"
      //   );
      // }

      const fcm_token = await generateFCMToken();
      
      let response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.TEACHER_LOGIN}`,
        {
          email: emailId,
          password: password,
          fcm_token: fcm_token || "",
        }
      );
      if (response.status === 200) {
        // ✅ Save token to localStorage
        localStorage.setItem("accessToken", response.data.access);
        localStorage.setItem("role", response.data.role);
        localStorage.setItem(
          "teacherData",
          JSON.stringify(response.data.teacher_data)
        );
        SuccessAlert(response.data.message);

        // console.log("Login Success:", response.data);

        // ✅ Redirect after login (example)
        navigate("/app/teacher-dashboard");
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      ErrorAlert(message);
    } finally {
      setLoading(false);
    }
  };

  const updateFormValue = ({ updateType, value }) => {
    setErrorMessage("");
    setLoginObj({ ...loginObj, [updateType]: value });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* LEFT – FORM */}
        <div className="p-10 md:p-12">
          <h2 className="text-3xl font-semibold mb-2">School Teacher Login</h2>
          <p className="text-gray-500 mb-4">
            Login to manage your teacher dashboard
          </p>

          {/* ✅ SINGLE FORM ONLY */}
          <form onSubmit={submitForm} className="space-y-3">
            {/* EMAIL */}
            <div>
              <label className="block text-sm font-medium">Email Address</label>
              <InputText
                type="email" // ✅ FIXED
                value={loginObj.emailId} // ✅ controlled input
                updateType="emailId"
                updateFormValue={updateFormValue}
              />
              {emailError && (
                <p className="text-sm text-red-500 mt-1">{emailError}</p>
              )}
            </div>

            {/* PASSWORD */}
            <div>
              <label className="block text-sm font-medium">Password</label>

              <div className="relative">
                <InputText
                  type={showPassword ? "text" : "password"}
                  value={loginObj.password} // ✅ controlled input
                  updateType="password"
                  updateFormValue={updateFormValue}
                  className="pr-12"
                />

                {/* 👁 Toggle */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 mt-2  top-1/2 -translate-y-1/2
                         h-8 w-8 rounded-md border border-gray-300
                         flex items-center justify-center
                         text-gray-500 hover:border-gray-500 transition"
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>

              {errorMessage && (
                <p className="text-sm text-red-500 mt-1">{errorMessage}</p>
              )}
            </div>

            {/* FORGOT PASSWORD */}
            <div className="text-right mr-10">
              <Link
                to="/teacher/forget-pass" // ✅ FIXED URL
                className="text-sm text-indigo-600 hover:underline"
              >
                Forgot Password?
              </Link>
            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={loading}
              className={`w-[90%] py-2 rounded-lg text-white
            bg-gradient-to-r from-violet-500 to-indigo-600 transition
            ${loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"}
          `}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>

        {/* RIGHT – ILLUSTRATION */}
        <div className="hidden md:flex items-center justify-center bg-gradient-to-b from-indigo-400 to-violet-500">
          <div className="w-72 h-72 overflow-hidden flex items-center justify-center">
            <img
              src="/final_logo_ver2.png"
              alt="School Admin Login"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolTeacherLogin;
